import React from 'react';
import styled from '@benzinga/themetron';
import { Rate } from '@benzinga/core-ui';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { Product } from '@benzinga/content-manager';
import { Icon } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import { ProductFeatures } from '../Product/ProductFeatures';
import { BzImage } from '@benzinga/image';

interface ProductCardOptions {
  cta_button_text?: string;
  hide_attributes?: boolean;
  layout?: string;
  product?: boolean;
  product_link_type?: string;
  show_best_for?: boolean | string;
  show_commissions?: boolean | string;
  show_company_name?: boolean | string;
  show_cons?: boolean | string;
  show_pros?: boolean | string;
  show_star_rating?: boolean | string;
}

export interface CompareProductsSideBySideProps {
  options: ProductCardOptions;
  products: Product[];
}
interface TableRowProps {
  options?: ProductCardOptions;
  products: Product[];
}

export const CompareProductsSideBySide: React.FC<CompareProductsSideBySideProps> = ({
  options = {},
  products = [],
}) => {
  if (!Array.isArray(products)) return null;
  return (
    <Container className="compare-products-side-by-side">
      <table>
        <tbody>
          {Array.isArray(products) && (
            <>
              <TableRowOne products={products} />
              <TableRowTwo options={options} products={products} />
              <TableRowThree options={options} products={products} />
              <TableRowFour options={options} products={products} />
              <TableRowFive options={options} products={products} />
            </>
          )}
        </tbody>
      </table>
    </Container>
  );
};

const TableRowOne: React.FC<TableRowProps> = ({ products }) => (
  <tr>
    {products?.map(product => (
      <td key={product.id}>
        <div className="table-row-one">
          <SmartLink href={product.link} label="Product Card Image">
            <BzImage alt={product.name} height={75} src={product.image} width={100} />
          </SmartLink>
          <Rate allowHalf className="rating-component" readOnly={true} value={product.rating} />
          {product.review ? <a href={product.review}>READ REVIEW</a> : null}
        </div>
      </td>
    ))}
  </tr>
);

const TableRowTwo: React.FC<TableRowProps> = ({ options, products }) => (
  <tr>
    {products?.map(product => (
      <td key={product.id}>
        {options?.show_best_for !== '0' && product.details?.best_for && (
          <ProductFeatures className="best-for" features={product.details.best_for} type="best-for" />
        )}
      </td>
    ))}
  </tr>
);

const TableRowThree: React.FC<TableRowProps> = ({ options, products }) => (
  <tr>
    {products?.map(product => (
      <td key={product.id}>
        {options?.show_pros !== '0' && product.details?.pros && (
          <ProductFeatures className="pros" features={product.details.pros} type="pros" />
        )}
      </td>
    ))}
  </tr>
);

const TableRowFour: React.FC<TableRowProps> = ({ options, products }) => (
  <tr>
    {products?.map(product => (
      <td key={product.id}>
        {options?.show_cons !== '0' && product.details?.cons && (
          <ProductFeatures className="cons" features={product.details.cons} type="cons" />
        )}
      </td>
    ))}
  </tr>
);

const TableRowFive: React.FC<TableRowProps> = ({ options, products }) => (
  <tr>
    {products?.map(product => (
      <td key={product.id}>
        <SmartLink className="cta-link-button" href={product.link} label="Product Card CTA">
          {product?.button_text || options?.cta_button_text || 'Open an Account'} <Icon icon={faLock} />
        </SmartLink>
      </td>
    ))}
  </tr>
);

const Container = styled.div`
  &.compare-products-side-by-side {
    background-color: ${({ theme }) => theme.colorPalette.white};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid #d6d6d6;
    overflow: auto;
    font-size: 14px;
    margin: 1rem 0;

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow.lg};
    }

    > table {
      margin: 0;
      width: 100%;

      tbody {
        tr {
          white-space: pre-wrap;
          border-bottom: solid 1px #ddd;
          border-top: none !important;

          td {
            width: 350px;
            max-width: 350px;
            padding: 10px;
            border-left: none !important;
            border-right: solid 1px #ddd;
            vertical-align: top;
            &:last-of-type {
              border-right: none !important;
            }
          }

          &:first-of-type {
          }

          &:last-of-type {
            border-bottom: none;
            td {
              border-bottom: none;
            }
          }
        }
      }
    }

    .table-row-one {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 15px 0;

      img {
        width: 200px;
      }
      a {
        color: #2ca2d1;
        font-size: 0.9rem;
        font-weight: bold;
      }
    }

    h4 {
      font-size: ${({ theme }) => theme.fontSize.sm};
      margin-bottom: 0 !important;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }

    .single-item-view {
      ul {
        list-style-type: none;
        margin-left: 0;
        margin-bottom: 0;
      }
    }

    .best-for-span {
      font-size: ${({ theme }) => theme.fontSize.lg};
    }

    .cta-link-button {
      display: inline-block;
      height: 2.5rem;
      width: 100%;
      white-space: nowrap;
      border-radius: 0.25rem;
      background-color: rgb(0, 45, 69);
      padding: 0 0.875rem;
      text-align: center;
      font-family: sans-serif;
      font-size: 0.875rem;
      line-height: 1.25rem;
      font-weight: 700;
      text-transform: uppercase;
      line-height: 2.5rem;
      letter-spacing: 0.1em;
      color: #ffffff;
      text-decoration: none;
      cursor: pointer;
      box-shadow: ${({ theme }) => theme.shadow.sm};

      &:hover {
        box-shadow: ${({ theme }) => theme.shadow.md};
        position: relative;
        bottom: 2px;
      }

      > span {
        display: inline-block;
        margin-bottom: -2px;
      }
    }
  }
`;
